import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useUserRole } from '../../../context/UserRoleProvider'
import { api } from '../../../lib/api'
import { Button } from '../../../components/ui/button'
import { Input } from '../../../components/ui/input'
import { Loader2, Edit, Save, X, ChevronLeft, ChevronRight, Upload, Plus } from 'lucide-react'

export default function AdminParishes() {
  const navigate = useNavigate()
  const { isSuperAdmin } = useUserRole()
  const [parishes, setParishes] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [editingParishId, setEditingParishId] = useState(null)
  const [editingData, setEditingData] = useState({})
  const [saving, setSaving] = useState(false)
  const [uploadingImage, setUploadingImage] = useState(false)
  const [imagePreview, setImagePreview] = useState(null)
  const [selectedImage, setSelectedImage] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [paginationMeta, setPaginationMeta] = useState(null)
  const pageSize = 50 // Items per page

  const fetchParishes = async (page = 1) => {
    try {
      setLoading(true)
      setError(null)
      // Fetch parishes with pagination (max 100 per request)
      const response = await api.searchParishes({ 
        page, 
        limit: pageSize, 
        search: searchQuery || undefined 
      })
      setParishes(response.data || [])
      setPaginationMeta(response.meta || null)
      setCurrentPage(page)
      setLoading(false)
    } catch (err) {
      console.error('Failed to fetch parishes:', err)
      setError(err.message || 'Failed to load parishes')
      setLoading(false)
    }
  }

  // Initial load
  useEffect(() => {
    if (!isSuperAdmin) {
      setError('You do not have permission to access this page.')
      setLoading(false)
      return
    }
    fetchParishes(1)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperAdmin])

  // Handle search with debounce
  useEffect(() => {
    if (!isSuperAdmin) return
    
    const timeoutId = setTimeout(() => {
      setCurrentPage(1) // Reset to first page on search
      fetchParishes(1)
    }, 500)
    return () => clearTimeout(timeoutId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery])

  // Handle page changes
  useEffect(() => {
    if (!isSuperAdmin || currentPage === 1) return // Skip if initial load or already on page 1
    fetchParishes(currentPage)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage])

  const handleEdit = (parish) => {
    setEditingParishId(parish.id)
    setEditingData({
      name: parish.name || '',
      address: parish.address || '',
      city: parish.city || '',
      state: parish.state || '',
      zipCode: parish.zipCode || '',
      email: parish.email || '',
      phoneNumber: parish.phoneNumber || '',
      description: parish.description || '',
    })
    setImagePreview(parish.imageUrl || null)
    setSelectedImage(null)
  }

  const handleCancelEdit = () => {
    setEditingParishId(null)
    setEditingData({})
    setImagePreview(null)
    setSelectedImage(null)
  }

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif']
    if (!validTypes.includes(file.type)) {
      setError('Invalid file type. Please select a JPEG, PNG, WebP, or GIF image.')
      return
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setError('File size exceeds 5MB. Please select a smaller image.')
      return
    }

    setSelectedImage(file)
    setError(null)

    // Create preview
    const reader = new FileReader()
    reader.onloadend = () => {
      setImagePreview(reader.result)
    }
    reader.readAsDataURL(file)
  }

  const handleRemoveImage = () => {
    setSelectedImage(null)
    // Reset to original image if it exists
    const currentParish = parishes.find(p => p.id === editingParishId)
    setImagePreview(currentParish?.imageUrl || null)
  }

  const handleImageUpload = async (parishId) => {
    if (!selectedImage || !parishId) return

    try {
      setUploadingImage(true)
      setError(null)

      const updatedParish = await api.uploadParishImage(parishId, selectedImage)
      
      // Update the parish in the list
      setParishes(prev => prev.map(p => 
        p.id === parishId ? updatedParish : p
      ))
      
      setImagePreview(updatedParish.imageUrl)
      setSelectedImage(null)
      
      // Refresh the list to get updated data
      await fetchParishes(currentPage)
    } catch (err) {
      console.error('Failed to upload image:', err)
      setError(err.message || 'Failed to upload image')
    } finally {
      setUploadingImage(false)
    }
  }

  const handleSave = async (parishId) => {
    try {
      setSaving(true)
      setError(null)
      
      // Upload image first if one is selected but not yet uploaded
      if (selectedImage) {
        try {
          setUploadingImage(true)
          const updatedParish = await api.uploadParishImage(parishId, selectedImage)
          setImagePreview(updatedParish.imageUrl)
          setSelectedImage(null)
        } catch (err) {
          console.error('Failed to upload image:', err)
          setError(err.message || 'Failed to upload image')
          setSaving(false)
          setUploadingImage(false)
          return
        } finally {
          setUploadingImage(false)
        }
      }
      
      await api.updateParish(parishId, editingData)
      
      await fetchParishes(currentPage)
      setEditingParishId(null)
      setEditingData({})
      setImagePreview(null)
      setSelectedImage(null)
    } catch (err) {
      console.error('Failed to update parish:', err)
      setError(err.message || 'Failed to update parish')
    } finally {
      setSaving(false)
    }
  }

  const handleInputChange = (field, value) => {
    setEditingData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  if (!isSuperAdmin) {
    return (
      <div className="bg-red-50 border border-red-200 rounded p-6">
        <p className="text-red-600">You do not have permission to access this page.</p>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 text-gold animate-spin" />
      </div>
    )
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center space-x-4">
          <div>
            <h1 className="text-3xl font-bold font-display text-charcoal mb-2">Manage Parishes</h1>
            <p className="text-charcoal-muted">View and edit all parishes</p>
          </div>
        </div>
        <Button
          onClick={() => navigate('/app/admin/parishes/create')}
          className="bg-gold text-black hover:bg-gold/90"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create New Parish
        </Button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded p-4 mb-6">
          <p className="text-red-600">{error}</p>
        </div>
      )}

      <div className="mb-6">
        <Input
          type="text"
          placeholder="Search parishes by name, city, or state..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="max-w-md"
        />
      </div>

      <div className="space-y-4">
        {parishes.length === 0 ? (
          <div className="bg-white border border-surface-border rounded p-8 text-center">
            <p className="text-charcoal-muted">No parishes found.</p>
          </div>
        ) : (
          parishes.map((parish) => (
            <div
              key={parish.id}
              className="bg-white border border-surface-border rounded p-6"
            >
              {editingParishId === parish.id ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-charcoal-muted mb-1">Name</label>
                      <Input
                        value={editingData.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-charcoal-muted mb-1">Email</label>
                      <Input
                        type="email"
                        value={editingData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-charcoal-muted mb-1">Address</label>
                      <Input
                        value={editingData.address}
                        onChange={(e) => handleInputChange('address', e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-charcoal-muted mb-1">City</label>
                      <Input
                        value={editingData.city}
                        onChange={(e) => handleInputChange('city', e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-charcoal-muted mb-1">State</label>
                      <Input
                        value={editingData.state}
                        onChange={(e) => handleInputChange('state', e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-charcoal-muted mb-1">Zip Code</label>
                      <Input
                        value={editingData.zipCode}
                        onChange={(e) => handleInputChange('zipCode', e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-charcoal-muted mb-1">Phone</label>
                      <Input
                        value={editingData.phoneNumber}
                        onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-charcoal-muted mb-1">Description</label>
                    <textarea
                      value={editingData.description}
                      onChange={(e) => handleInputChange('description', e.target.value)}
                      className="w-full px-3 py-2 bg-white border border-surface-border rounded text-charcoal focus:outline-none focus:ring-2 focus:ring-gold"
                      rows="3"
                    />
                  </div>
                  
                  {/* Image Upload Section */}
                  <div>
                    <label className="block text-sm font-medium text-charcoal-muted mb-2">Parish Image</label>
                    <div className="space-y-4">
                      {/* Image Preview */}
                      {imagePreview && (
                        <div className="relative w-full max-w-md">
                          <img
                            src={imagePreview}
                            alt="Parish preview"
                            className="w-full h-48 object-cover rounded border border-surface-border"
                            onError={(e) => {
                              e.target.style.display = 'none'
                            }}
                          />
                          {selectedImage && (
                            <div className="absolute top-2 right-2">
                              <span className="px-2 py-1 text-xs bg-gold/90 text-black rounded">
                                New Image
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* File Input and Upload Button */}
                      <div className="flex items-center space-x-2">
                        <input
                          type="file"
                          accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
                          onChange={handleImageSelect}
                          className="hidden"
                          id={`image-input-${parish.id}`}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          className="flex-1 text-charcoal border-gold/50 hover:border-gold"
                          onClick={() => {
                            const input = document.getElementById(`image-input-${parish.id}`)
                            if (input) {
                              input.click()
                            }
                          }}
                        >
                          <Upload className="w-4 h-4 mr-2" />
                          {selectedImage ? 'Change Image' : 'Select Image'}
                        </Button>
                        
                        {selectedImage && (
                          <>
                            <Button
                              type="button"
                              onClick={() => handleImageUpload(parish.id)}
                              disabled={uploadingImage}
                              className="bg-gold text-black hover:bg-gold/90"
                            >
                              {uploadingImage ? (
                                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                              ) : (
                                <Upload className="w-4 h-4 mr-2" />
                              )}
                              Upload
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              onClick={handleRemoveImage}
                              className="text-charcoal hover:text-charcoal-muted"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                      </div>
                      
                      {!imagePreview && !selectedImage && (
                        <p className="text-xs text-charcoal-muted">No image uploaded. Select an image to upload.</p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Button
                      type="button"
                      onClick={() => handleSave(parish.id)}
                      disabled={saving || uploadingImage}
                      className="bg-gold text-black hover:bg-gold/90"
                    >
                      {saving ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <Save className="w-4 h-4 mr-2" />
                      )}
                      Save
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={handleCancelEdit}
                      disabled={uploadingImage}
                      className="text-charcoal hover:text-charcoal-muted"
                    >
                      <X className="w-4 h-4 mr-2" />
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <div>
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-start space-x-4">
                        {/* Parish Image */}
                        {parish.imageUrl && (
                          <div className="flex-shrink-0">
                            <img
                              src={parish.imageUrl}
                              alt={parish.name}
                              className="w-24 h-24 object-cover rounded border border-surface-border"
                              onError={(e) => {
                                e.target.style.display = 'none'
                              }}
                            />
                          </div>
                        )}
                        <div className="flex-1">
                          <h3 
                            onClick={() => navigate(`/app/admin/parishes/${parish.id}`)}
                            className="text-xl font-semibold font-display text-charcoal mb-2 hover:text-charcoal transition-colors cursor-pointer"
                          >
                            {parish.name}
                          </h3>
                          <div className="space-y-1 text-charcoal-muted text-sm">
                            {parish.address && <p>{parish.address}</p>}
                            {(parish.city || parish.state || parish.zipCode) && (
                              <p>
                                {[parish.city, parish.state, parish.zipCode].filter(Boolean).join(', ')}
                              </p>
                            )}
                            {parish.email && <p>Email: {parish.email}</p>}
                            {parish.phoneNumber && <p>Phone: {parish.phoneNumber}</p>}
                            {parish.description && <p className="mt-2">{parish.description}</p>}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => navigate(`/app/admin/parishes/${parish.id}`)}
                        className="text-charcoal hover:text-charcoal-muted"
                      >
                        View Details
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleEdit(parish)}
                        className="text-charcoal hover:text-charcoal-muted"
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        Edit
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Pagination Controls */}
      {paginationMeta && paginationMeta.totalPages > 1 && (
        <div className="mt-8 flex items-center justify-between">
          <div className="text-sm text-charcoal-muted">
            Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, paginationMeta.totalItems)} of {paginationMeta.totalItems} parishes
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchParishes(currentPage - 1)}
              disabled={!paginationMeta.hasPrevPage || loading}
              className="text-charcoal border-gold/50 hover:border-gold"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Previous
            </Button>
            <div className="flex items-center space-x-1">
              {Array.from({ length: Math.min(5, paginationMeta.totalPages) }, (_, i) => {
                let pageNum
                if (paginationMeta.totalPages <= 5) {
                  pageNum = i + 1
                } else if (currentPage <= 3) {
                  pageNum = i + 1
                } else if (currentPage >= paginationMeta.totalPages - 2) {
                  pageNum = paginationMeta.totalPages - 4 + i
                } else {
                  pageNum = currentPage - 2 + i
                }
                return (
                  <Button
                    key={pageNum}
                    variant={currentPage === pageNum ? "default" : "outline"}
                    size="sm"
                    onClick={() => fetchParishes(pageNum)}
                    disabled={loading}
                    className={
                      currentPage === pageNum
                        ? "bg-gold text-black hover:bg-gold/90"
                        : "text-charcoal border-gold/50 hover:border-gold"
                    }
                  >
                    {pageNum}
                  </Button>
                )
              })}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchParishes(currentPage + 1)}
              disabled={!paginationMeta.hasNextPage || loading}
              className="text-charcoal border-gold/50 hover:border-gold"
            >
              Next
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
